import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import api from '../services/api.js';

const HRMSContext = createContext();

const initialState = {
  workers: [],
  attendance: [],
  leaves: [],
  leaveTypes: [],
  holidays: [],
  shifts: [],
  payroll: [],
  processStages: [],
  countries: [],
  states: [],
  cities: [],
  companyIdTypes: [],
  categories: [],
  stoneTypes: [],
  currencies: [],
  diamondTypes: []
};

function hrmsReducer(state, action) {
  switch (action.type) {
    case 'SET_WORKERS':
      return { ...state, workers: action.payload };
    case 'SET_ATTENDANCE':
      return { ...state, attendance: action.payload };
    case 'SET_LEAVES':
      return { ...state, leaves: action.payload };
    case 'SET_LEAVE_TYPES':
      return { ...state, leaveTypes: action.payload };
    case 'SET_HOLIDAYS':
      return { ...state, holidays: action.payload };
    case 'SET_SHIFTS':
      return { ...state, shifts: action.payload };
    case 'SET_PAYROLL':
      return { ...state, payroll: action.payload };
    case 'SET_PROCESS_STAGES':
      return { ...state, processStages: action.payload };
    case 'SET_COUNTRIES':
      return { ...state, countries: action.payload };
    case 'SET_STATES':
      return { ...state, states: action.payload };
    case 'SET_CITIES':
      return { ...state, cities: action.payload };
    case 'SET_COMPANY_ID_TYPES':
      return { ...state, companyIdTypes: action.payload };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_STONE_TYPES':
      return { ...state, stoneTypes: action.payload };
    case 'SET_CURRENCIES':
      return { ...state, currencies: action.payload };
    case 'SET_DIAMOND_TYPES':
      return { ...state, diamondTypes: action.payload };
    case 'ADD_LEAVE':
      return { ...state, leaves: [...state.leaves, action.payload] };
    case 'UPDATE_LEAVE_STATUS':
      return {
        ...state,
        leaves: state.leaves.map(leave =>
          leave.id === action.payload.id
            ? { ...leave, status: action.payload.status }
            : leave
        )
      };
    default:
      return state;
  }
}

export function HRMSProvider({ children }) {
  const [state, dispatch] = useReducer(hrmsReducer, initialState);

  // Load all data (only for initial app load)
  const loadData = async () => {
    try {
      const [workers, attendance, leaves, leaveTypes, holidays, shifts, payroll, processStages, companyIdTypes, categories, stoneTypes] = await Promise.all([
        api.workers.getAll().catch(() => []),
        api.attendance.getAll().catch(() => []),
        api.leaves.getAll().catch(() => []),
        api.leaveTypes.getAll().catch(() => []),
        api.holidays.getAll().catch(() => []),
        api.shifts.getAll().catch(() => []),
        api.payroll.getAll().catch(() => []),
        api.processStages.getAll().catch(() => []),
        api.companyIdTypes.getAll().catch(() => []),
        api.categories.getAll().catch(() => []),
        api.stoneTypes.getAll().catch(() => [])
      ]);
      
      dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
      dispatch({ type: 'SET_ATTENDANCE', payload: Array.isArray(attendance) ? attendance : [] });
      dispatch({ type: 'SET_LEAVES', payload: Array.isArray(leaves) ? leaves : [] });
      dispatch({ type: 'SET_LEAVE_TYPES', payload: Array.isArray(leaveTypes) ? leaveTypes : [] });
      dispatch({ type: 'SET_HOLIDAYS', payload: Array.isArray(holidays) ? holidays : [] });
      dispatch({ type: 'SET_SHIFTS', payload: Array.isArray(shifts) ? shifts : [] });
      dispatch({ type: 'SET_PAYROLL', payload: Array.isArray(payroll) ? payroll : [] });
      dispatch({ type: 'SET_PROCESS_STAGES', payload: Array.isArray(processStages) ? processStages : [] });
      dispatch({ type: 'SET_COMPANY_ID_TYPES', payload: Array.isArray(companyIdTypes) ? companyIdTypes : [] });
      dispatch({ type: 'SET_CATEGORIES', payload: Array.isArray(categories) ? categories : [] });
      dispatch({ type: 'SET_STONE_TYPES', payload: Array.isArray(stoneTypes) ? stoneTypes : [] });
    } catch (error) {
      console.error('Error loading data:', error);
      dispatch({ type: 'SET_WORKERS', payload: [] });
      dispatch({ type: 'SET_ATTENDANCE', payload: [] });
      dispatch({ type: 'SET_LEAVES', payload: [] });
      dispatch({ type: 'SET_LEAVE_TYPES', payload: [] });
      dispatch({ type: 'SET_HOLIDAYS', payload: [] });
      dispatch({ type: 'SET_SHIFTS', payload: [] });
      dispatch({ type: 'SET_PAYROLL', payload: [] });
      dispatch({ type: 'SET_PROCESS_STAGES', payload: [] });
      dispatch({ type: 'SET_COMPANY_ID_TYPES', payload: [] });
      dispatch({ type: 'SET_CATEGORIES', payload: [] });
      dispatch({ type: 'SET_STONE_TYPES', payload: [] });
    }
  };

  // Specific load functions for each module with conditional loading
  const loadWorkers = useCallback(async () => {
    try {
      const workers = await api.workers.getAll();
      dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
    } catch (error) {
      console.error('Error loading workers:', error);
      dispatch({ type: 'SET_WORKERS', payload: [] });
    }
  }, []);

  const loadAttendance = useCallback(async () => {
    try {
      // Load workers only if not already loaded
      if (state.workers.length === 0) {
        const workers = await api.workers.getAll().catch(() => []);
        dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
      }
      
      const attendance = await api.attendance.getAll().catch(() => []);
      dispatch({ type: 'SET_ATTENDANCE', payload: Array.isArray(attendance) ? attendance : [] });
    } catch (error) {
      console.error('Error loading attendance:', error);
      dispatch({ type: 'SET_ATTENDANCE', payload: [] });
    }
  }, [state.workers.length]);

  const loadLeaves = useCallback(async () => {
    try {
      // Load workers only if not already loaded
      if (state.workers.length === 0) {
        const workers = await api.workers.getAll().catch(() => []);
        dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
      }
      
      const [leaves, leaveTypes] = await Promise.all([
        api.leaves.getAll().catch(() => []),
        api.leaveTypes.getAll().catch(() => [])
      ]);
      dispatch({ type: 'SET_LEAVES', payload: Array.isArray(leaves) ? leaves : [] });
      dispatch({ type: 'SET_LEAVE_TYPES', payload: Array.isArray(leaveTypes) ? leaveTypes : [] });
    } catch (error) {
      console.error('Error loading leaves:', error);
      dispatch({ type: 'SET_LEAVES', payload: [] });
      dispatch({ type: 'SET_LEAVE_TYPES', payload: [] });
    }
  }, [state.workers.length]);

  const loadHolidays = useCallback(async () => {
    try {
      const holidays = await api.holidays.getAll();
      dispatch({ type: 'SET_HOLIDAYS', payload: Array.isArray(holidays) ? holidays : [] });
    } catch (error) {
      console.error('Error loading holidays:', error);
      dispatch({ type: 'SET_HOLIDAYS', payload: [] });
    }
  }, []);

  const loadShifts = useCallback(async () => {
    try {
      const shifts = await api.shifts.getAll();
      dispatch({ type: 'SET_SHIFTS', payload: Array.isArray(shifts) ? shifts : [] });
    } catch (error) {
      console.error('Error loading shifts:', error);
      dispatch({ type: 'SET_SHIFTS', payload: [] });
    }
  }, []);

  const loadLeaveTypes = useCallback(async () => {
    try {
      const leaveTypes = await api.leaveTypes.getAll();
      dispatch({ type: 'SET_LEAVE_TYPES', payload: Array.isArray(leaveTypes) ? leaveTypes : [] });
    } catch (error) {
      console.error('Error loading leave types:', error);
      dispatch({ type: 'SET_LEAVE_TYPES', payload: [] });
    }
  }, []);

  const loadPayroll = useCallback(async () => {
    try {
      // Load workers only if not already loaded
      if (state.workers.length === 0) {
        const workers = await api.workers.getAll().catch(() => []);
        dispatch({ type: 'SET_WORKERS', payload: Array.isArray(workers) ? workers : [] });
      }
      
      const payroll = await api.payroll.getAll().catch(() => []);
      dispatch({ type: 'SET_PAYROLL', payload: Array.isArray(payroll) ? payroll : [] });
    } catch (error) {
      console.error('Error loading payroll:', error);
      dispatch({ type: 'SET_PAYROLL', payload: [] });
    }
  }, [state.workers.length]);

  const loadProcessStages = useCallback(async () => {
    try {
      const processStages = await api.processStages.getAll();
      dispatch({ type: 'SET_PROCESS_STAGES', payload: Array.isArray(processStages) ? processStages : [] });
    } catch (error) {
      console.error('Error loading process stages:', error);
      dispatch({ type: 'SET_PROCESS_STAGES', payload: [] });
    }
  }, []);

  const loadCountries = useCallback(async () => {
    try {
      const countries = await api.countries.getAll();
      dispatch({ type: 'SET_COUNTRIES', payload: Array.isArray(countries) ? countries : [] });
    } catch (error) {
      console.error('Error loading countries:', error);
      dispatch({ type: 'SET_COUNTRIES', payload: [] });
    }
  }, []);

  const loadStates = useCallback(async () => {
    try {
      const states = await api.states.getAll();
      dispatch({ type: 'SET_STATES', payload: Array.isArray(states) ? states : [] });
    } catch (error) {
      console.error('Error loading states:', error);
      dispatch({ type: 'SET_STATES', payload: [] });
    }
  }, []);

  const loadCities = useCallback(async () => {
    try {
      const cities = await api.cities.getAll();
      dispatch({ type: 'SET_CITIES', payload: Array.isArray(cities) ? cities : [] });
    } catch (error) {
      console.error('Error loading cities:', error);
      dispatch({ type: 'SET_CITIES', payload: [] });
    }
  }, []);
  const loadCompanyIdTypes = useCallback(async () => {
    try {
      const companyIdTypes = await api.companyIdTypes.getAll();
      dispatch({ type: 'SET_COMPANY_ID_TYPES', payload: Array.isArray(companyIdTypes) ? companyIdTypes : [] });
    } catch (error) {
      console.error('Error loading company ID types:', error);
      dispatch({ type: 'SET_COMPANY_ID_TYPES', payload: [] });
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const categories = await api.categories.getAll();
      dispatch({ type: 'SET_CATEGORIES', payload: Array.isArray(categories) ? categories : [] });
    } catch (error) {
      console.error('Error loading categories:', error);
      dispatch({ type: 'SET_CATEGORIES', payload: [] });
    }
  }, []);

  const loadStoneTypes = useCallback(async () => {
    try {
      const stoneTypes = await api.stoneTypes.getAll();
      dispatch({ type: 'SET_STONE_TYPES', payload: Array.isArray(stoneTypes) ? stoneTypes : [] });
    } catch (error) {
      console.error('Error loading stone types:', error);
      dispatch({ type: 'SET_STONE_TYPES', payload: [] });
    }
  }, []);

  const loadCurrencies = useCallback(async () => {
    try {
      const currencies = await api.currencies.getAll();
      dispatch({ type: 'SET_CURRENCIES', payload: Array.isArray(currencies) ? currencies : [] });
    } catch (error) {
      console.error('Error loading currencies:', error);
      dispatch({ type: 'SET_CURRENCIES', payload: [] });
    }
  }, []);

  const loadDiamondTypes = useCallback(async () => {
    try {
      const diamondTypes = await api.diamondTypes.getAll();
      dispatch({ type: 'SET_DIAMOND_TYPES', payload: Array.isArray(diamondTypes) ? diamondTypes : [] });
    } catch (error) {
      console.error('Error loading diamond types:', error);
      dispatch({ type: 'SET_DIAMOND_TYPES', payload: [] });
    }
  }, []);

  // Remove automatic data loading on startup
  // Data will be loaded only when specific pages are accessed

  return (
    <HRMSContext.Provider value={{ 
      state, 
      dispatch, 
      loadData, 
      loadWorkers, 
      loadAttendance, 
      loadLeaves, 
      loadLeaveTypes,
      loadHolidays, 
      loadShifts, 
      loadPayroll,
      loadProcessStages,
      loadCountries,
      loadStates,
      loadCities,
      loadCompanyIdTypes,
      loadCategories,
      loadStoneTypes,
      loadCurrencies,
      loadDiamondTypes
    }}>
      {children}
    </HRMSContext.Provider>
  );
}

export function useHRMS() {
  const context = useContext(HRMSContext);
  if (!context) {
    throw new Error('useHRMS must be used within HRMSProvider');
  }
  return context;
}